import * as React from "react"
import {
  Alert02Icon,
  Cancel01Icon,
  CheckmarkCircle02Icon,
  Clock01Icon,
  CloudUploadIcon,
  File01Icon,
  Image01Icon,
  Pdf01Icon,
  RefreshIcon,
  Video01Icon,
  WifiDisconnected01Icon,
} from "@hugeicons-pro/core-stroke-rounded"
import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react"

import {
  Attachment,
  AttachmentAction,
  AttachmentActions,
  AttachmentContent,
  AttachmentDescription,
  AttachmentMedia,
  AttachmentTitle,
} from "@car/ui/components/attachment"
import { Progress } from "@car/ui/components/progress"
import {
  UploadError,
  UploadJob,
  fileType,
  formatBytes,
  formatEta,
  formatPercent,
  formatSpeed,
  isAbortError,
  xhrPut,
  type UploadSnapshot,
  type UploadTransport,
  type UploadedFile,
} from "@car/ui/lib/upload"
import { cn, SEP } from "@car/ui/lib/utils"

// The uploader: files go from the browser straight to S3 (see @car/ui/lib/upload),
// several at a time, each with progress, speed, time left, automatic retries,
// an offline wait, cancel and manual retry (which resumes from the last sent part).
//
//   <UploadProvider transport={...}>      once per app (its storage procedures)
//   useUploads({ policy, onUploaded })    the queue behind any uploader UI
//   <FileDropzone uploads={...} />        click, drag and drop, paste
//   <UploadList uploads={...} />          one row per file

const fa = new Intl.NumberFormat("fa-IR")

// ---------------------------------------------------------------------------
// transport and policy
// ---------------------------------------------------------------------------

const UploadTransportContext = React.createContext<UploadTransport | null>(null)

function UploadProvider({
  transport,
  children,
}: {
  transport: UploadTransport
  children: React.ReactNode
}) {
  return <UploadTransportContext.Provider value={transport}>{children}</UploadTransportContext.Provider>
}

function useUploadTransport() {
  const transport = React.useContext(UploadTransportContext)
  if (!transport) throw new Error("useUploads needs an <UploadProvider> above it")
  return transport
}

/** What may be uploaded for one purpose (the app passes its storage-config entry) */
type UploadPolicy = {
  purpose: string
  types: readonly string[]
  maxBytes: number
  /** allowed types as people read them, e.g. «JPG، PNG یا WebP» */
  typesLabel: string
}

// ---------------------------------------------------------------------------
// the queue
// ---------------------------------------------------------------------------

type UploadStatus = "queued" | "active" | "done" | "error"

type UploadItem = {
  id: string
  file: File
  type: string
  /** object URL for images and videos, to preview before anything is uploaded */
  preview: string | null
  status: UploadStatus
  snapshot: UploadSnapshot
  error: string | null
  retryable: boolean
  result: UploadedFile | null
}

type UseUploadsOptions = {
  policy: UploadPolicy
  /** files the queue may still take; undefined = no limit */
  maxFiles?: number
  /** files uploading at once */
  concurrency?: number
  /** runs after the server verified the file; throw an Error to show it on the row */
  onUploaded?: (file: UploadedFile) => void | Promise<void>
  /** drop finished rows (a field that shows its value instead) */
  removeDone?: boolean
  onBusyChange?: (busy: boolean) => void
}

type Uploads = ReturnType<typeof useUploads>

const idleSnapshot = (total: number): UploadSnapshot => ({
  phase: "preparing",
  loaded: 0,
  total,
  speed: 0,
  eta: null,
  attempt: 0,
  retryAt: null,
})

function useUploads(options: UseUploadsOptions) {
  const transport = useUploadTransport()
  const [items, setItems] = React.useState<UploadItem[]>([])
  const [rejections, setRejections] = React.useState<string[]>([])
  const jobs = React.useRef(new Map<string, { job: UploadJob; controller: AbortController | null }>())
  const latest = React.useRef({ options, items })
  latest.current = { options, items }

  const patch = React.useCallback((id: string, change: Partial<UploadItem>) => {
    setItems((list) => list.map((item) => (item.id === id ? { ...item, ...change } : item)))
  }, [])

  const forget = React.useCallback((id: string) => {
    jobs.current.delete(id)
    setItems((list) => {
      const gone = list.find((item) => item.id === id)
      if (gone?.preview) URL.revokeObjectURL(gone.preview)
      return list.filter((item) => item.id !== id)
    })
  }, [])

  const start = React.useCallback(
    (id: string) => {
      const entry = jobs.current.get(id)
      if (!entry || entry.controller) return
      const controller = new AbortController()
      entry.controller = controller
      patch(id, { status: "active", error: null })
      entry.job.run(controller.signal).then(
        async (file) => {
          entry.controller = null
          if (controller.signal.aborted) return
          patch(id, { status: "done", result: file })
          try {
            await latest.current.options.onUploaded?.(file)
          } catch (error) {
            patch(id, {
              status: "error",
              error: error instanceof Error ? error.message : "ثبت فایل انجام نشد.",
              retryable: false,
            })
            return
          }
          if (latest.current.options.removeDone) forget(id)
        },
        (error: Error) => {
          entry.controller = null
          if (controller.signal.aborted || isAbortError(error)) return
          const failure = error instanceof UploadError ? error : new UploadError("بارگذاری انجام نشد.", true)
          patch(id, { status: "error", error: failure.message, retryable: failure.retryable })
        },
      )
    },
    [forget, patch],
  )

  // a small scheduler: queued files start as others finish
  const concurrency = options.concurrency ?? 2
  React.useEffect(() => {
    const running = items.filter((item) => item.status === "active").length
    const next = items.filter((item) => item.status === "queued").slice(0, Math.max(0, concurrency - running))
    for (const item of next) start(item.id)
  }, [items, concurrency, start])

  /** Queues what the policy allows; returns (and shows) why the rest was refused */
  const add = React.useCallback(
    (files: readonly File[]) => {
      const { policy, maxFiles } = latest.current.options
      const current = latest.current.items
      const problems: string[] = []
      let room = maxFiles === undefined ? Number.POSITIVE_INFINITY : maxFiles - current.length
      const accepted: UploadItem[] = []
      for (const file of files) {
        const type = fileType(file)
        if (!policy.types.includes(type)) {
          problems.push(`«${file.name}» پذیرفته نیست؛ فقط ${policy.typesLabel}.`)
        } else if (file.size === 0) {
          problems.push(`«${file.name}» خالی است.`)
        } else if (file.size > policy.maxBytes) {
          problems.push(`حجم «${file.name}» (${formatBytes(file.size)}) بیشتر از ${formatBytes(policy.maxBytes)} است.`)
        } else if (
          [...current, ...accepted].some(
            (item) =>
              item.file.name === file.name &&
              item.file.size === file.size &&
              item.file.lastModified === file.lastModified,
          )
        ) {
          problems.push(`«${file.name}» همین حالا در فهرست است.`)
        } else if (room <= 0) {
          problems.push(
            maxFiles === 1
              ? "فقط یک فایل انتخاب کنید."
              : `بیش از ${fa.format(Math.max(0, maxFiles ?? 0))} فایل دیگر جا نمی‌شود.`,
          )
          break
        } else {
          room -= 1
          const id = crypto.randomUUID()
          const job = new UploadJob(file, {
            purpose: policy.purpose,
            transport,
            put: xhrPut,
            onChange: (snapshot) => patch(id, { snapshot }),
          })
          jobs.current.set(id, { job, controller: null })
          accepted.push({
            id,
            file,
            type,
            preview: type.startsWith("image/") || type.startsWith("video/") ? URL.createObjectURL(file) : null,
            status: "queued",
            snapshot: idleSnapshot(file.size),
            error: null,
            retryable: false,
            result: null,
          })
        }
      }
      if (accepted.length > 0) setItems((list) => [...list, ...accepted])
      setRejections(problems)
      return problems
    },
    [patch, transport],
  )

  /** Stops a file for good: requests aborted, the server forgets its bytes */
  const cancel = React.useCallback(
    (id: string) => {
      const entry = jobs.current.get(id)
      entry?.controller?.abort()
      void entry?.job.discard()
      forget(id)
    },
    [forget],
  )

  /** Tries a failed file again from where it stopped */
  const retry = React.useCallback((id: string) => patch(id, { status: "queued", error: null }), [patch])

  const busy = items.some((item) => item.status === "queued" || item.status === "active")

  // leaving the page mid-upload loses it: the browser asks first
  React.useEffect(() => {
    if (!busy) return
    const warn = (event: BeforeUnloadEvent) => event.preventDefault()
    window.addEventListener("beforeunload", warn)
    return () => window.removeEventListener("beforeunload", warn)
  }, [busy])

  React.useEffect(() => {
    latest.current.options.onBusyChange?.(busy)
  }, [busy])

  // closing the form cancels what it was still uploading (finished files are left alone)
  React.useEffect(() => {
    const running = jobs.current
    return () => {
      for (const item of latest.current.items) {
        if (item.preview) URL.revokeObjectURL(item.preview)
        if (item.status === "done") continue
        const entry = running.get(item.id)
        entry?.controller?.abort()
        void entry?.job.discard()
      }
      running.clear()
    }
  }, [])

  return {
    items,
    busy,
    rejections,
    clearRejections: React.useCallback(() => setRejections([]), []),
    add,
    cancel,
    retry,
    /** hides a finished or failed row */
    dismiss: forget,
    policy: options.policy,
  }
}

// ---------------------------------------------------------------------------
// dropping, picking and pasting files
// ---------------------------------------------------------------------------

type DragState = "idle" | "accept" | "reject"

/** Drop target props for any element, with a live verdict on what is being dragged */
function useFileDrop(input: { types: readonly string[]; disabled?: boolean; onFiles: (files: File[]) => void }) {
  const [drag, setDrag] = React.useState<DragState>("idle")
  const depth = React.useRef(0)
  const { types, disabled, onFiles } = input

  const verdict = (transfer: DataTransfer): DragState => {
    const kinds = Array.from(transfer.items).filter((item) => item.kind === "file")
    if (kinds.length === 0) return transfer.types.includes("Files") ? "accept" : "reject"
    // some browsers hide the type until the drop; give those the benefit of the doubt
    return kinds.some((item) => item.type === "" || types.includes(item.type)) ? "accept" : "reject"
  }

  return {
    drag,
    handlers: {
      onDragEnter: (event: React.DragEvent) => {
        if (disabled || !event.dataTransfer.types.includes("Files")) return
        event.preventDefault()
        depth.current += 1
        setDrag(verdict(event.dataTransfer))
      },
      onDragOver: (event: React.DragEvent) => {
        if (disabled || !event.dataTransfer.types.includes("Files")) return
        event.preventDefault()
        event.dataTransfer.dropEffect = drag === "reject" ? "none" : "copy"
      },
      onDragLeave: (event: React.DragEvent) => {
        if (disabled || !event.dataTransfer.types.includes("Files")) return
        depth.current = Math.max(0, depth.current - 1)
        if (depth.current === 0) setDrag("idle")
      },
      onDrop: (event: React.DragEvent) => {
        if (disabled || !event.dataTransfer.types.includes("Files")) return
        event.preventDefault()
        depth.current = 0
        setDrag("idle")
        const files = Array.from(event.dataTransfer.files)
        if (files.length > 0) onFiles(files)
      },
      onPaste: (event: React.ClipboardEvent) => {
        if (disabled) return
        const files = Array.from(event.clipboardData.files)
        if (files.length === 0) return
        event.preventDefault()
        onFiles(files)
      },
    },
  }
}

/** A hidden file input and a function that opens it */
function useFilePicker(input: {
  types: readonly string[]
  multiple?: boolean
  onFiles: (files: File[]) => void
}) {
  const ref = React.useRef<HTMLInputElement>(null)
  const element = (
    <input
      ref={ref}
      type="file"
      className="sr-only"
      tabIndex={-1}
      aria-hidden
      accept={input.types.join(",")}
      multiple={input.multiple}
      onChange={(event) => {
        const files = Array.from(event.target.files ?? [])
        // the same file can be chosen again after a cancel
        event.target.value = ""
        if (files.length > 0) input.onFiles(files)
      }}
    />
  )
  return { element, open: () => ref.current?.click() }
}

/**
 * Types, size cap, count. Each piece is one unbreakable box: a line wrapping inside
 * «تصویر (JPG، PNG، WebP) یا PDF» lets the bidi algorithm flip its parentheses.
 */
function PolicyHint({ policy, maxFiles }: { policy: UploadPolicy; maxFiles?: number }) {
  const pieces = [
    policy.typesLabel,
    `حداکثر ${formatBytes(policy.maxBytes)}`,
    maxFiles !== undefined && maxFiles > 1 ? `تا ${fa.format(maxFiles)} فایل` : null,
  ].filter((piece): piece is string => Boolean(piece))
  return (
    <>
      {pieces.map((piece, index) => (
        <React.Fragment key={piece}>
          {index > 0 ? SEP : null}
          <span className="inline-block">{piece}</span>
        </React.Fragment>
      ))}
    </>
  )
}

function FileDropzone({
  uploads,
  multiple = false,
  maxFiles,
  disabled = false,
  title,
  description,
  variant = "default",
  className,
}: {
  uploads: Uploads
  multiple?: boolean
  /** shown in the hint; the limit itself is useUploads' maxFiles */
  maxFiles?: number
  disabled?: boolean
  title?: string
  description?: string
  /** default: a roomy area; compact: one line; fill: covers its (positioned) parent */
  variant?: "default" | "compact" | "fill"
  className?: string
}) {
  const hintId = React.useId()
  const { policy } = uploads
  const onFiles = (files: File[]) => uploads.add(multiple ? files : files.slice(0, 1))
  const picker = useFilePicker({ types: policy.types, multiple, onFiles })
  const { drag, handlers } = useFileDrop({ types: policy.types, disabled, onFiles })
  const images = policy.types.every((type) => type.startsWith("image/"))
  const idleTitle = title ?? (multiple ? "فایل‌ها را اینجا رها کنید" : "فایل را اینجا رها کنید")
  const heading =
    drag === "accept"
      ? "رها کنید تا بارگذاری شود"
      : drag === "reject"
        ? "این نوع فایل پذیرفته نیست"
        : idleTitle

  return (
    <div className={cn("grid gap-2", variant === "fill" && "absolute inset-0", className)}>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={`${idleTitle}، یا برای انتخاب فایل Enter بزنید`}
        aria-disabled={disabled || undefined}
        aria-describedby={hintId}
        data-drag={drag}
        data-variant={variant}
        onClick={() => !disabled && picker.open()}
        onKeyDown={(event) => {
          if (disabled || (event.key !== "Enter" && event.key !== " ")) return
          event.preventDefault()
          picker.open()
        }}
        {...handlers}
        className={cn(
          "group/dropzone relative flex cursor-pointer items-center gap-3 rounded-2xl border-2 border-dashed border-input bg-muted/30 text-center outline-none transition-[background-color,border-color,box-shadow] duration-150",
          "hover:border-primary/50 hover:bg-primary/[0.03] focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-ring",
          "data-[drag=accept]:border-primary data-[drag=accept]:bg-primary/5 data-[drag=reject]:border-destructive data-[drag=reject]:bg-destructive/5",
          "aria-disabled:cursor-not-allowed aria-disabled:opacity-60",
          variant === "compact" ? "flex-row justify-start px-4 py-3 text-start" : "flex-col justify-center px-6 py-8",
          variant === "fill" && "h-full rounded-[inherit] py-4",
        )}
      >
        <span
          className={cn(
            "grid shrink-0 place-items-center rounded-full bg-background text-primary shadow-xs ring-1 ring-border transition-transform duration-200 motion-reduce:transition-none",
            "group-data-[drag=accept]/dropzone:-translate-y-0.5 group-data-[drag=accept]/dropzone:scale-110 group-data-[drag=reject]/dropzone:text-destructive",
            variant === "compact" ? "size-9" : "size-12",
          )}
        >
          <HugeiconsIcon
            icon={drag === "reject" ? Alert02Icon : CloudUploadIcon}
            strokeWidth={1.8}
            className={variant === "compact" ? "size-4.5" : "size-6"}
          />
        </span>
        <span className="grid min-w-0 gap-1">
          <span
            className={cn(
              "font-bold",
              variant === "compact" ? "text-sm" : "text-sm sm:text-base",
              drag === "reject" && "text-destructive",
            )}
          >
            {heading}
          </span>
          <span id={hintId} className="text-xs text-muted-foreground">
            {drag === "idle" ? (
              <>
                <span className="inline-block">{description ?? "یا برای انتخاب کلیک کنید"}</span>
                <span className="mx-1.5 text-border" aria-hidden>
                  |
                </span>
                <PolicyHint policy={policy} maxFiles={maxFiles} />
                {images && variant !== "compact" ? (
                  <span className="mt-1 block text-xs">
                    می‌توانید تصویر را جای‌گذاری هم کنید (<kbd className="font-sans">Ctrl</kbd>+
                    <kbd className="font-sans">V</kbd>)
                  </span>
                ) : null}
              </>
            ) : (
              <PolicyHint policy={policy} maxFiles={maxFiles} />
            )}
          </span>
        </span>
      </div>
      {picker.element}
      <UploadRejections uploads={uploads} />
    </div>
  )
}

function UploadRejections({ uploads, className }: { uploads: Uploads; className?: string }) {
  if (uploads.rejections.length === 0) return null
  return (
    <div
      role="alert"
      className={cn(
        "flex items-start gap-2 rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive",
        className,
      )}
    >
      <HugeiconsIcon icon={Alert02Icon} strokeWidth={1.8} className="mt-0.5 size-4 shrink-0" />
      <ul className="grid flex-1 gap-0.5">
        {uploads.rejections.map((problem) => (
          <li key={problem}>{problem}</li>
        ))}
      </ul>
      <button
        type="button"
        onClick={uploads.clearRejections}
        className="-m-1 rounded-md p-1 opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-2"
        aria-label="بستن پیام"
      >
        <HugeiconsIcon icon={Cancel01Icon} strokeWidth={1.8} className="size-3.5" />
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// progress, in words
// ---------------------------------------------------------------------------

/** Re-renders once a second while `active`, for retry countdowns */
function useTicker(active: boolean) {
  const [, setTick] = React.useState(0)
  React.useEffect(() => {
    if (!active) return
    const timer = setInterval(() => setTick((tick) => tick + 1), 1000)
    return () => clearInterval(timer)
  }, [active])
}

const iconFor = (type: string): IconSvgElement =>
  type.startsWith("image/")
    ? Image01Icon
    : type.startsWith("video/")
      ? Video01Icon
      : type === "application/pdf"
        ? Pdf01Icon
        : File01Icon

/** One line that says where a file is, what it moves at and how long it will take */
function describeUpload(item: UploadItem) {
  const { snapshot } = item
  if (item.status === "error") return item.error ?? "بارگذاری انجام نشد."
  if (item.status === "done") return `بارگذاری شد${SEP}${formatBytes(item.file.size)}`
  if (item.status === "queued") return `در صف${SEP}${formatBytes(item.file.size)}`
  switch (snapshot.phase) {
    case "preparing":
      return "آماده‌سازی…"
    case "processing":
      return "بررسی نهایی فایل…"
    case "offline":
      return "اینترنت قطع است؛ با وصل شدن ادامه می‌دهد"
    case "retrying": {
      const seconds = Math.max(0, Math.ceil(((snapshot.retryAt ?? 0) - Date.now()) / 1000))
      return `ارسال قطع شد؛ تلاش دوباره ${seconds > 0 ? `تا ${fa.format(seconds)} ثانیه دیگر` : "…"}`
    }
    default:
      // every byte left the device; the storage has not answered yet
      if (snapshot.total > 0 && snapshot.loaded >= snapshot.total) return "ارسال شد؛ در انتظار تأیید فضای ذخیره‌سازی…";
      return [
        `${formatBytes(snapshot.loaded)} از ${formatBytes(snapshot.total)}`,
        snapshot.speed > 0 ? formatSpeed(snapshot.speed) : null,
        snapshot.eta !== null && snapshot.speed > 0 ? `${formatEta(snapshot.eta)} مانده` : null,
      ]
        .filter(Boolean)
        .join(SEP)
  }
}

const attachmentState = (item: UploadItem) =>
  item.status === "error"
    ? "error"
    : item.status === "done"
      ? "done"
      : item.status === "queued"
        ? "idle"
        : item.snapshot.phase === "processing"
          ? "processing"
          : "uploading"

function UploadRow({ uploads, item }: { uploads: Uploads; item: UploadItem }) {
  const active = item.status === "active" || item.status === "queued"
  const { snapshot } = item
  const percent = snapshot.total > 0 ? Math.min(100, (snapshot.loaded / snapshot.total) * 100) : 0
  const phaseIcon =
    item.status === "active" && snapshot.phase === "offline"
      ? WifiDisconnected01Icon
      : item.status === "active" && snapshot.phase === "retrying"
        ? Clock01Icon
        : null
  return (
    <Attachment state={attachmentState(item)} className="w-full max-w-none" aria-label={item.file.name}>
      <AttachmentMedia variant={item.preview && item.type.startsWith("image/") ? "image" : "icon"}>
        {item.preview && item.type.startsWith("image/") ? (
          <img src={item.preview} alt="" />
        ) : (
          <HugeiconsIcon icon={iconFor(item.type)} strokeWidth={1.8} />
        )}
        {item.status === "done" ? (
          <span className="absolute -end-0.5 -bottom-0.5 grid size-4 place-items-center rounded-full bg-background text-primary">
            <HugeiconsIcon icon={CheckmarkCircle02Icon} strokeWidth={2} className="size-4" />
          </span>
        ) : null}
      </AttachmentMedia>
      <AttachmentContent className="grid gap-1">
        <div className="flex min-w-0 items-baseline gap-2">
          <AttachmentTitle dir="auto" className="flex-1 text-start">
            {item.file.name}
          </AttachmentTitle>
          {item.status === "active" && snapshot.phase === "uploading" ? (
            <span className="shrink-0 text-xs font-bold text-primary tabular-nums">
              {formatPercent(snapshot.loaded, snapshot.total)}
            </span>
          ) : null}
        </div>
        <AttachmentDescription className="flex items-center gap-1 whitespace-normal">
          {phaseIcon ? <HugeiconsIcon icon={phaseIcon} strokeWidth={1.8} className="size-3.5 shrink-0" /> : null}
          <span>{describeUpload(item)}</span>
        </AttachmentDescription>
        {active ? (
          <Progress
            value={item.status === "queued" ? 0 : percent}
            aria-label={`پیشرفت بارگذاری ${item.file.name}`}
            className={cn(
              "gap-0 [&_[data-slot=progress-track]]:h-1",
              snapshot.phase === "processing" && "[&_[data-slot=progress-indicator]]:animate-pulse",
              (snapshot.phase === "retrying" || snapshot.phase === "offline") &&
                "[&_[data-slot=progress-indicator]]:bg-muted-foreground/50",
            )}
          />
        ) : null}
      </AttachmentContent>
      <AttachmentActions className="gap-0.5">
        {item.status === "error" && item.retryable ? (
          <AttachmentAction aria-label={`تلاش دوباره برای ${item.file.name}`} title="تلاش دوباره" onClick={() => uploads.retry(item.id)}>
            <HugeiconsIcon icon={RefreshIcon} strokeWidth={1.8} />
          </AttachmentAction>
        ) : null}
        <AttachmentAction
          aria-label={active ? `لغو بارگذاری ${item.file.name}` : `بستن ${item.file.name}`}
          title={active ? "لغو" : "بستن"}
          onClick={() => (active ? uploads.cancel(item.id) : uploads.dismiss(item.id))}
        >
          <HugeiconsIcon icon={Cancel01Icon} strokeWidth={1.8} />
        </AttachmentAction>
      </AttachmentActions>
    </Attachment>
  )
}

/** Every row, a combined header when several files move at once, and a screen-reader log */
function UploadList({ uploads, className }: { uploads: Uploads; className?: string }) {
  const { items } = uploads
  useTicker(items.some((item) => item.status === "active" && item.snapshot.phase === "retrying"))
  const moving = items.filter((item) => item.status === "active" || item.status === "queued")
  const total = moving.reduce((sum, item) => sum + item.snapshot.total, 0)
  const loaded = moving.reduce((sum, item) => sum + item.snapshot.loaded, 0)
  const speed = moving.reduce((sum, item) => sum + item.snapshot.speed, 0)
  const finished = items.filter((item) => item.status === "done").length
  const failed = items.filter((item) => item.status === "error").length
  if (items.length === 0) return null
  return (
    <div className={cn("grid gap-2", className)}>
      {moving.length > 1 ? (
        <div className="grid gap-1.5 rounded-xl bg-muted/50 px-3 py-2">
          <div className="flex items-center justify-between gap-2 text-xs">
            <span className="font-bold">{fa.format(moving.length)} فایل در حال بارگذاری</span>
            <span className="text-muted-foreground tabular-nums">
              {formatPercent(loaded, total)}
              {speed > 0 ? `${SEP}${formatSpeed(speed)}` : ""}
              {speed > 0 && total > loaded ? `${SEP}${formatEta((total - loaded) / speed)} مانده` : ""}
            </span>
          </div>
          <Progress value={total > 0 ? (loaded / total) * 100 : 0} aria-label="پیشرفت کل بارگذاری" className="gap-0" />
        </div>
      ) : null}
      <ul className="grid gap-2">
        {items.map((item) => (
          <li key={item.id}>
            <UploadRow uploads={uploads} item={item} />
          </li>
        ))}
      </ul>
      <p className="sr-only" aria-live="polite">
        {moving.length > 0
          ? `${fa.format(moving.length)} فایل در حال بارگذاری`
          : `${fa.format(finished)} فایل بارگذاری شد${failed > 0 ? `، ${fa.format(failed)} فایل ناموفق` : ""}`}
      </p>
    </div>
  )
}

export {
  FileDropzone,
  UploadList,
  UploadProvider,
  UploadRejections,
  UploadRow,
  describeUpload,
  iconFor,
  useFileDrop,
  useFilePicker,
  useTicker,
  useUploadTransport,
  useUploads,
  type UploadItem,
  type UploadPolicy,
  type Uploads,
}
